import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowRight,
    CheckCircle2,
    Euro,
    MapPin,
    Sparkles,
    ShieldCheck,
    Users,
    FileText,
    Download,
    Mail,
    Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { api, formatApiError } from "../lib/api";
import { useCheck } from "../contexts/CheckContext";
import { useAuth } from "../contexts/AuthContext";
import { REGION_COSTS } from "../lib/checkData";
import { toast } from "sonner";

const PACKAGES = {
    Bronze: {
        price: "Erste Analyse",
        desc: "Kompakte Erstorientierung für klar strukturierte Fälle.",
    },
    Silber: {
        price: "Empfohlen",
        desc: "Vertiefte Analyse inklusive Einkommens- und Vermögensprüfung.",
    },
    Gold: {
        price: "Premium",
        desc: "Strategische Begleitung bei Kassen, Behörden, Heim und Familie.",
    },
};

export default function DashboardPage() {
    const { answers, teaser, reset } = useCheck();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [saved, setSaved] = useState(null);
    const [history, setHistory] = useState([]);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    // Persist teaser as real case after login
    useEffect(() => {
        const run = async () => {
            if (teaser && Object.keys(answers || {}).length > 0) {
                try {
                    const { data } = await api.post("/cases", { answers });
                    setSaved(data);
                } catch (e) {
                    toast.error(formatApiError(e, "Fall konnte nicht gespeichert werden."));
                }
            }
            try {
                const { data } = await api.get("/cases");
                setHistory(data || []);
                if (!teaser && data?.length) setSaved(data[0]);
            } catch {
                /* ignore */
            }
        };
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const region = answers?.region || saved?.answers?.region || "baden-wuerttemberg";
    const regionInfo = REGION_COSTS[region] || REGION_COSTS["baden-wuerttemberg"];
    const costBreakdown = useMemo(
        () => ({
            pflege: Math.round(regionInfo.eigenanteil_total * 0.45),
            wohnen: Math.round(regionInfo.eigenanteil_total * 0.3),
            invest: Math.round(regionInfo.eigenanteil_total * 0.15),
            sonstiges: Math.round(regionInfo.eigenanteil_total * 0.1),
        }),
        [regionInfo],
    );

    const risk = saved?.risk_level || teaser?.risk_level || "medium";
    const pkg = saved?.package_recommendation || teaser?.package_recommendation || "Silber";
    const summary = saved?.result_summary || teaser?.result_summary ||
        "Ihre Situation enthält mehrere klärungsbedürftige Punkte.";
    const details = saved?.result_details || [];

    const requestAnalysis = () => {
        navigate("/kontakt", { state: { package: pkg, case_id: saved?.case_id } });
    };

    const activeCaseId = saved?.case_id || history[0]?.case_id;

    const downloadReport = async () => {
        if (!activeCaseId) {
            toast.error("Noch kein gespeicherter Fall vorhanden.");
            return;
        }
        setDownloadingPdf(true);
        try {
            const res = await api.get(`/cases/${activeCaseId}/report`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Pflegeheim-Kosten_Kurzreport_${activeCaseId}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
            toast.success("Report heruntergeladen");
        } catch (e) {
            toast.error(formatApiError(e, "Download fehlgeschlagen"));
        } finally {
            setDownloadingPdf(false);
        }
    };

    const emailReport = async () => {
        if (!activeCaseId) {
            toast.error("Noch kein gespeicherter Fall vorhanden.");
            return;
        }
        setSendingEmail(true);
        try {
            const { data } = await api.post(`/cases/${activeCaseId}/report/email`, {});
            toast.success(`Report an ${data.sent_to} versendet`);
        } catch (e) {
            toast.error(formatApiError(e, "Versand fehlgeschlagen"));
        } finally {
            setSendingEmail(false);
        }
    };

    return (
        <div className="min-h-[80vh] bg-brand-50/20 py-8 sm:py-12">
            <div className="container max-w-5xl">
                <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-600">
                            <Sparkles className="h-4 w-4" /> Ihr Vollergebnis
                        </div>
                        <h1 className="mt-1 font-display text-3xl font-bold sm:text-4xl">Einschätzung &amp; nächste Schritte</h1>
                    </div>
                    <Button
                        variant="outline"
                        className="rounded-full"
                        onClick={() => {
                            reset();
                            navigate("/check");
                        }}
                        data-testid="dashboard-restart"
                    >
                        Neuen Check starten
                    </Button>
                </div>

                {/* Kurzfazit */}
                <div className="mt-6 rounded-3xl border border-ink-300/30 bg-white p-6 shadow-softer sm:p-8">
                    <div className="flex flex-wrap items-center gap-3">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            risk === "high"
                                ? "bg-amber-100 text-amber-800"
                                : risk === "medium"
                                ? "bg-brand-100 text-brand-800"
                                : "bg-emerald-100 text-emerald-800"
                        }`}>
                            Risikolevel: {risk === "high" ? "Hoch" : risk === "medium" ? "Mittel" : "Niedrig"}
                        </span>
                        <span className="rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700">
                            Empfehlung: {pkg}
                        </span>
                        <span className="rounded-full bg-ink-100 px-3 py-1 text-xs text-ink-700 bg-muted">
                            Region: {regionInfo.label}
                        </span>
                    </div>
                    <h2 className="mt-4 font-display text-2xl font-bold">Kurzfazit</h2>
                    <p className="mt-2 text-ink-700">{summary}</p>

                    {details?.length > 0 && (
                        <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                            {details.map((d, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-ink-700">
                                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-brand-500" />
                                    <span>{d}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Was bedeutet das? */}
                <div className="mt-6 grid gap-6 lg:grid-cols-5">
                    <div className="rounded-3xl border border-ink-300/30 bg-white p-6 lg:col-span-3">
                        <h3 className="font-display text-xl font-bold">Was bedeutet das konkret?</h3>
                        <p className="mt-3 text-sm text-ink-700">
                            Verdient ein Kind beispielsweise 150.000&nbsp;€ brutto im Jahr, verfügt die
                            pflegebedürftige Person nur noch über geringe Mittel und reichen Rente sowie
                            Leistungen nicht aus, kann eine vertiefte Prüfung der Situation besonders wichtig
                            werden. Gerade in Regionen mit hohen Heimkosten – etwa in Baden-Württemberg –
                            entsteht schnell erheblicher Klärungsbedarf.
                        </p>
                        <p className="mt-3 text-sm text-ink-700">
                            Die 100.000-€-Grenze bezieht sich auf das Jahresbrutto je Kind. Liegt kein Kind
                            darüber, muss dem Sozialamt in der Regel nichts erstattet werden. Das bedeutet
                            jedoch nicht, dass keine Beratung nötig wäre: Heimkosten, Sozialhilfe-Anträge und
                            Vermögensfragen bleiben oft komplex.
                        </p>
                    </div>

                    {/* Kostenstruktur */}
                    <div className="rounded-3xl border border-ink-300/30 bg-white p-6 lg:col-span-2">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-600">
                            <MapPin className="h-4 w-4" /> {regionInfo.label}
                        </div>
                        <h3 className="mt-1 font-display text-xl font-bold">Beispielhafte Kostenstruktur</h3>
                        <div className="mt-4 space-y-3">
                            {[
                                { k: "Pflegebedingter Eigenanteil", v: costBreakdown.pflege },
                                { k: "Unterkunft &amp; Verpflegung", v: costBreakdown.wohnen },
                                { k: "Investitionskosten", v: costBreakdown.invest },
                                { k: "Weitere Posten", v: costBreakdown.sonstiges },
                            ].map((row) => (
                                <div key={row.k} className="rounded-xl bg-brand-50/60 p-3">
                                    <div className="flex justify-between text-sm">
                                        <span dangerouslySetInnerHTML={{ __html: row.k }} />
                                        <span className="font-semibold">ca. {row.v} €</span>
                                    </div>
                                    <div className="mt-2 h-1.5 w-full rounded-full bg-white">
                                        <div
                                            className="h-1.5 rounded-full bg-brand-500"
                                            style={{ width: `${(row.v / regionInfo.eigenanteil_total) * 100}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 rounded-xl bg-amber-50 p-3 text-xs text-amber-900">
                            Orientierungswerte, regional sehr unterschiedlich. Nicht verbindlich, ersetzen keine
                            individuelle Prüfung.
                        </div>
                    </div>
                </div>

                {/* Paketempfehlung */}
                <div className="mt-6 rounded-3xl bg-gradient-to-br from-brand-500 to-brand-600 p-7 text-white shadow-soft sm:p-10">
                    <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-center">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-white/90">
                                Für Ihre Situation empfehlen wir
                            </div>
                            <h3 className="mt-2 font-display text-3xl font-bold">{pkg} – {PACKAGES[pkg]?.price}</h3>
                            <p className="mt-2 max-w-lg text-white/90">{PACKAGES[pkg]?.desc}</p>
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row">
                            <Button
                                onClick={requestAnalysis}
                                className="h-12 rounded-full bg-white px-6 font-semibold text-brand-700 hover:bg-white/90"
                                data-testid="dashboard-request-package"
                            >
                                {pkg} anfragen <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                            <Link to="/pakete">
                                <Button
                                    variant="outline"
                                    className="h-12 rounded-full border-white/40 bg-transparent text-white hover:bg-white/10"
                                    data-testid="dashboard-all-packages"
                                >
                                    Alle Pakete vergleichen
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* PDF Kurzreport */}
                <div className="mt-6 rounded-3xl border border-ink-300/30 bg-white p-6 sm:p-8" data-testid="dashboard-report-card">
                    <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-start gap-4">
                            <span className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                                <FileText className="h-6 w-6" />
                            </span>
                            <div>
                                <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">
                                    Ihr persönlicher Kurzreport
                                </div>
                                <h3 className="mt-1 font-display text-xl font-bold">1-Seiten-PDF mit Ihrer Einschätzung</h3>
                                <p className="mt-1 max-w-xl text-sm text-ink-500">
                                    Kompakt zum Mitnehmen oder Ausdrucken: Kurzfazit, Kostenbeispiel Ihrer
                                    Region, nächste Schritte und passende Paketempfehlung.
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row md:flex-col lg:flex-row">
                            <Button
                                onClick={downloadReport}
                                disabled={downloadingPdf || !activeCaseId}
                                variant="outline"
                                className="h-12 rounded-full gap-2"
                                data-testid="report-download-btn"
                            >
                                {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                                PDF herunterladen
                            </Button>
                            <Button
                                onClick={emailReport}
                                disabled={sendingEmail || !activeCaseId}
                                className="h-12 rounded-full bg-brand-500 hover:bg-brand-600 gap-2"
                                data-testid="report-email-btn"
                            >
                                {sendingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                                Per E-Mail senden
                            </Button>
                        </div>
                    </div>
                    {user?.email && (
                        <div className="mt-4 text-xs text-ink-500">
                            E-Mail-Versand an: <strong>{user.email}</strong>
                        </div>
                    )}
                </div>

                {/* Nächste Schritte */}
                <div className="mt-6 grid gap-6 sm:grid-cols-3">
                    {[
                        {
                            icon: Euro,
                            title: "Kosten strukturieren",
                            text: "Heimkosten, Pflegekasse, Eigenanteil und mögliche Finanzierungslücken sauber erfassen.",
                        },
                        {
                            icon: Users,
                            title: "Familienkonstellation",
                            text: "Individuelle Leistungsfähigkeit je Kind prüfen – keine pauschale Gleichverteilung.",
                        },
                        {
                            icon: ShieldCheck,
                            title: "Behörden-Dialog",
                            text: "Gespräche mit Sozialamt, Pflegekasse und Heim fachlich vorbereiten.",
                        },
                    ].map((s) => (
                        <div key={s.title} className="rounded-3xl border border-ink-300/30 bg-white p-6">
                            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                                <s.icon className="h-5 w-5" />
                            </div>
                            <h4 className="mt-3 font-display text-lg font-semibold">{s.title}</h4>
                            <p className="mt-1 text-sm text-ink-500">{s.text}</p>
                        </div>
                    ))}
                </div>

                {/* Historie */}
                {history.length > 1 && (
                    <div className="mt-10 rounded-3xl border border-ink-300/30 bg-white p-6">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-600">
                            <FileText className="h-4 w-4" /> Ergebnis-Historie
                        </div>
                        <ul className="mt-3 divide-y divide-ink-300/30">
                            {history.map((h) => (
                                <li key={h.case_id} className="flex items-center justify-between py-3 text-sm">
                                    <span className="text-ink-700">
                                        {new Date(h.created_at).toLocaleString("de-DE")} – Risiko: {h.risk_level}, Paket: {h.package_recommendation}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}
